import React, { useState, useMemo } from 'react';
import {
  Users,
  Calendar,
  Clock,
  Video,
  MapPin,
  DollarSign,
  UserCheck,
  Tag,
} from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty, UserMiniCard, StatusBadge, getGenericStatusVariant } from './shared';
import { useUserGroupSessions } from '../../hooks/useUserTabs';
import { GroupSessionHosted, GroupSessionAttended, GroupSessionsData } from '../../types/userTabs';
import { cn, formatDateTime, formatNumber, formatCents, formatRelativeTime } from '../../utils/helpers';

type RoleFilter = 'all' | 'hosted' | 'attended';

const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const formatMode = (mode: string): string => {
  if (mode === 'IN_PERSON') return 'In Person';
  if (mode === 'VIRTUAL') return 'Virtual';
  return mode;
};

const getSessionStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  const s = status.toUpperCase();
  if (['COMPLETED', 'FINISHED'].includes(s)) return 'success';
  if (['SCHEDULED', 'UPCOMING', 'PENDING'].includes(s)) return 'warning';
  if (['CANCELLED', 'EXPIRED'].includes(s)) return 'error';
  if (['IN_PROGRESS', 'LIVE', 'ACTIVE'].includes(s)) return 'info';
  return 'default';
};

// ============================================================================
// Hosted Session Card
// ============================================================================

const HostedSessionCard: React.FC<{ session: GroupSessionHosted }> = ({ session }) => (
  <div className="p-4 rounded-lg border border-border/50 bg-muted/5 space-y-3">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-foreground truncate">{session.name}</p>
          <StatusBadge status={session.status} variant={getSessionStatusVariant(session.status)} />
        </div>
        {session.service && (
          <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {session.service.serviceName}
          </p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        {session.isFree ? (
          <span className="text-xs font-medium text-emerald-400">Free</span>
        ) : (
          <span className="text-sm font-bold text-foreground">
            {formatCents(session.priceCents)}
            <span className="text-[10px] text-muted-foreground font-normal"> /person</span>
          </span>
        )}
      </div>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Calendar className="h-3 w-3" />
        <span>{formatDateTime(session.startTime || session.scheduledAt)}</span>
      </div>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Clock className="h-3 w-3" />
        <span>{formatDuration(session.duration)}</span>
      </div>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Users className="h-3 w-3" />
        <span>
          {session.currentParticipants}/{session.maxParticipants}
          {session.registeredCount > 0 && (
            <span className="text-muted-foreground/60"> ({session.registeredCount} registered)</span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {session.mode === 'VIRTUAL' ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
        <span>{formatMode(session.mode)}</span>
      </div>
    </div>

    {/* Fill rate bar */}
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Fill rate</span>
        <span>{session.maxParticipants > 0 ? Math.round((session.currentParticipants / session.maxParticipants) * 100) : 0}%</span>
      </div>
      <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            session.currentParticipants >= session.maxParticipants ? 'bg-emerald-500' : 'bg-primary'
          )}
          style={{ width: `${session.maxParticipants > 0 ? Math.min(100, (session.currentParticipants / session.maxParticipants) * 100) : 0}%` }}
        />
      </div>
    </div>
  </div>
);

// ============================================================================
// Attended Session Card
// ============================================================================

const AttendedSessionCard: React.FC<{ participation: GroupSessionAttended }> = ({ participation }) => {
  const gs = participation.groupSession;
  return (
    <div className="p-4 rounded-lg border border-border/50 bg-muted/5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-foreground truncate">{gs.name}</p>
            <StatusBadge status={participation.status} variant={getSessionStatusVariant(participation.status)} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
            {gs.mode === 'VIRTUAL' ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
            {formatMode(gs.mode)} &middot; {formatDuration(gs.duration)}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          {participation.amountPaid > 0 ? (
            <span className="text-sm font-bold text-foreground">
              ${participation.amountPaid.toFixed(2)}
            </span>
          ) : (
            <span className="text-xs text-emerald-400">Free</span>
          )}
        </div>
      </div>

      {/* Host info */}
      <UserMiniCard
        user={{ id: gs.host.id, name: gs.host.name, profileImage: gs.host.profileImage }}
        subtitle="Host"
      />

      {/* Beneficiary if present */}
      {participation.beneficiary && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <UserCheck className="h-3 w-3" />
          <span>Beneficiary: <span className="text-foreground font-medium">{participation.beneficiary.name || `#${participation.beneficiary.id}`}</span></span>
        </div>
      )}

      {/* Timestamps */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span>Session: {formatDateTime(gs.startTime)}</span>
        {participation.registeredAt && <span>Registered: {formatRelativeTime(participation.registeredAt)}</span>}
        {participation.joinedAt && <span>Joined: {formatRelativeTime(participation.joinedAt)}</span>}
        {participation.cancelledAt && (
          <span className="text-red-400">Cancelled: {formatRelativeTime(participation.cancelledAt)}</span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const GroupSessionsTab: React.FC<{ userId: number }> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserGroupSessions(userId, true);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');

  const gsData = data as GroupSessionsData | null;

  const hosted = gsData?.hosted ?? [];
  const attended = gsData?.attended ?? [];
  const hostedTotal = gsData?.hostedTotal ?? 0;
  const attendedTotal = gsData?.attendedTotal ?? 0;

  const avgFillRate = useMemo(() => {
    if (hosted.length === 0) return 0;
    const total = hosted.reduce((sum, s) => sum + (s.maxParticipants > 0 ? s.currentParticipants / s.maxParticipants : 0), 0);
    return Math.round((total / hosted.length) * 100);
  }, [hosted]);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!gsData || (hosted.length === 0 && attended.length === 0)) {
    return <TabEmpty message="No group sessions found" icon={Users} />;
  }

  const filters: { key: RoleFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: hostedTotal + attendedTotal },
    { key: 'hosted', label: 'Hosted', count: hostedTotal },
    { key: 'attended', label: 'Attended', count: attendedTotal },
  ];

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard
          label="Sessions Hosted"
          value={formatNumber(hostedTotal)}
          icon={Video}
          color="text-violet-400"
          bgColor="bg-violet-400/10"
        />
        <MetricCard
          label="Sessions Attended"
          value={formatNumber(attendedTotal)}
          icon={Users}
          color="text-blue-400"
          bgColor="bg-blue-400/10"
        />
        <MetricCard
          label="Avg Fill Rate"
          value={`${avgFillRate}%`}
          icon={UserCheck}
          color="text-emerald-400"
          bgColor="bg-emerald-400/10"
          subtitle={`Across ${hosted.length} sessions`}
        />
        <MetricCard
          label="Total Revenue"
          value={formatCents(hosted.reduce((sum, s) => sum + (s.isFree ? 0 : s.priceCents * s.currentParticipants), 0))}
          icon={DollarSign}
          color="text-amber-400"
          bgColor="bg-amber-400/10"
          subtitle="From hosted sessions"
        />
      </div>

      {/* Role filter */}
      <div className="flex items-center gap-1 p-1 bg-muted/10 rounded-lg w-fit">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setRoleFilter(f.key)}
            className={cn(
              'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              roleFilter === f.key
                ? 'bg-primary/15 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'
            )}
          >
            {f.label}
            <span className="ml-1.5 text-[10px] opacity-70">{f.count}</span>
          </button>
        ))}
      </div>

      {/* Hosted Sessions */}
      {(roleFilter === 'all' || roleFilter === 'hosted') && (
        <SectionCard
          title="Hosted Group Sessions"
          icon={Video}
          subtitle={`${hostedTotal} session${hostedTotal !== 1 ? 's' : ''}`}
          collapsible
          defaultOpen
        >
          {hosted.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No hosted sessions</p>
          ) : (
            <div className="space-y-3">
              {hosted.map((session) => (
                <HostedSessionCard key={session.id} session={session} />
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* Attended Sessions */}
      {(roleFilter === 'all' || roleFilter === 'attended') && (
        <SectionCard
          title="Attended Group Sessions"
          icon={Users}
          subtitle={`${attendedTotal} session${attendedTotal !== 1 ? 's' : ''}`}
          collapsible
          defaultOpen
        >
          {attended.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No attended sessions</p>
          ) : (
            <div className="space-y-3">
              {attended.map((p) => (
                <AttendedSessionCard key={p.id} participation={p} />
              ))}
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
};

export default GroupSessionsTab;
